import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchProfile, login as loginRequest, logout as logoutRequest } from '../api/auth';
import { getStoredToken, storeAccessToken } from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      const data = await fetchProfile();
      setProfile(data);
      setError(null);
      return data;
    } catch (err) {
      setProfile(null);
      setError(err);
      throw err;
    } finally {
      setInitializing(false);
    }
  }, []);

  const login = useCallback(
    async (credentials) => {
      const data = await loginRequest(credentials);
      const token = data?.token?.accessToken || data?.token?.access_token;
      if (!token) {
        throw new Error('Authentication token missing in response.');
      }
      storeAccessToken(token);
      await loadProfile();
      return data;
    },
    [loadProfile]
  );

  const logout = useCallback(() => {
    logoutRequest();
    setProfile(null);
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setInitializing(false);
      return;
    }
    loadProfile().catch(() => {
      storeAccessToken(null);
      setInitializing(false);
    });
  }, [loadProfile]);

  const value = useMemo(
    () => ({
      profile,
      initializing,
      error,
      login,
      logout,
      reloadProfile: loadProfile,
      isAuthenticated: Boolean(profile),
    }),
    [profile, initializing, error, login, logout, loadProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
